import { Layout } from '@/components/Layout';
import { PlanSection, RequirementTable } from '@/components/PlanSection';
import { systemRequirements } from '@/data/projectPlan';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function Requirements() {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium':
        return 'bg-accent/50 text-accent-foreground border-accent';
      case 'low':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return '높음';
      case 'medium':
        return '중간';
      case 'low':
        return '낮음';
      default:
        return priority;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="relative h-[40vh] overflow-hidden">
          <img
            src={IMAGES.DASHBOARD_2}
            alt="시스템 요구사항"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          <div className="relative h-full flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center px-4"
            >
              <h1 className="text-5xl font-bold mb-4 text-foreground">시스템 요구사항</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                프로젝트 관리 시스템의 기능적/비기능적 요구사항 및 사용자 요구사항
              </p>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <PlanSection title="기능 요구사항" className="mb-16">
              <p className="text-muted-foreground mb-8">
                시스템이 제공해야 하는 핵심 기능들을 정의합니다. 사용자 관리, 학생 정보 관리, 팀 관리, 진도 관리, 평가 관리 등의 주요 기능을 포함합니다.
              </p>
              <div className="grid gap-6">
                {systemRequirements.functional.map((req, index) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow duration-200">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="font-mono text-xs">
                                {req.id}
                              </Badge>
                              <Badge className={getPriorityColor(req.priority)}>
                                {getPriorityLabel(req.priority)}
                              </Badge>
                            </div>
                            <CardTitle className="text-xl mb-2">{req.category}</CardTitle>
                            <CardDescription className="text-base font-medium text-foreground">
                              {req.requirement}
                            </CardDescription>
                          </div>
                          <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 ml-4" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground">{req.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </PlanSection>

            <PlanSection title="비기능 요구사항" className="mb-16">
              <p className="text-muted-foreground mb-8">
                시스템의 품질 속성과 제약사항을 정의합니다. 성능, 보안, 사용성, 호환성, 반응형 디자인 등의 요구사항을 포함합니다.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {systemRequirements.nonFunctional.map((req, index) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="font-mono text-xs">
                                {req.id}
                              </Badge>
                              <Badge className={getPriorityColor(req.priority)}>
                                {getPriorityLabel(req.priority)}
                              </Badge>
                            </div>
                            <CardTitle className="text-lg mb-2">{req.category}</CardTitle>
                          </div>
                          <AlertCircle className="w-5 h-5 text-accent flex-shrink-0 ml-4" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground">{req.requirement}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </PlanSection>

            <PlanSection title="사용자 요구사항">
              <p className="text-muted-foreground mb-8">
                교수와 학생 사용자의 관점에서 시스템이 제공해야 하는 기능과 경험을 정의합니다.
              </p>
              <div className="grid gap-6">
                {systemRequirements.userRequirements.map((req, index) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow duration-200">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="font-mono text-xs">
                                {req.id}
                              </Badge>
                              <Badge variant="secondary" className="gap-1">
                                <Users className="w-3 h-3" />
                                {req.role}
                              </Badge>
                            </div>
                            <CardTitle className="text-xl mb-2">{req.requirement}</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground">{req.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </PlanSection>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
